/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AclModel, L7BackendServiceModel } from '../../../models';
import { AclService } from '../../../services/components';
import { AfterViewInit, Component, TemplateRef, ViewChild } from '@angular/core';
import { BaseComponent } from '../../../../../../core/components';
import { ErrorResponseModel } from '../../../../../../core/models';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService
} from '../../../../../../core/services';
import { Layer7ApiService } from '../../../http';
import { LbRbacService } from '../../../services/utilities';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';

@Component({
    selector: 'app-lb-modals-hanging-acls',
    styleUrls: ['./hangingAcls.modal.component.scss'],
    templateUrl: './hangingAcls.modal.component.html',
})

export class HangingAclsModalComponent extends BaseComponent implements AfterViewInit {
    /**
     * Reference to the content of this component.
     */
    @ViewChild(TemplateRef) public templateRef: TemplateRef<any>;

    /**
     * External: Incoming hangingBackends
     */
    public hangingAcls: AclModel[] = [];

    /**
     * External: Incoming hangingBackends
     */
    public backends: L7BackendServiceModel[] = [];

    /**
     * External: Call back function in the parent to update the backends on the parent.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Array to store hanging backends
     */
    public tableRows: any = [];

    /**
     * Store of multiple selected rows in the table.
     */
    public selectedRows: any = [];

    /**
     * Sets modal submission state.
     */
    public submissionActive: boolean = false;

    /**
     * To store the progress message.
     */
    public progressMessage: string = '';

    /**
     * Store permissions.
     */
    public permissionsLookup: any = {};

    constructor(
        public lbRbacService: LbRbacService,
        public navigationService: NavigationService,
        private aclService: AclService,
        private layer7ApiService: Layer7ApiService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private notify: NotifyService,
        private rbacService: RbacService,
    ) {
        super();

        this.registerButtons();
    }

    /**
     * Logic that gets called after the view has initialised
     */
    public ngAfterViewInit() {
        this.populateTableRows();
        this.modalService.openModalInParent('hangingAclsModal');
    }

    /**
     * Triggered when a table row is selected.
     * @param selected
     */
    public onSelect(selected: any): void {
        this.selectedRows = selected;
    }

    /**
     * Set the selected rows to be equal to the total rows
     */
    public selectAllRows(): void {
        this.selectedRows = this.tableRows;
        // Kick the view
        this.tableRows = [...this.tableRows];
    }

    /**
     * Confirm delete of acls.
     * @param acls
     */
    public confirmDeleteAcl(acls: string | any[]): void {
        const message = acls.length > 1
            ? `Are you sure you want to delete ${this.selectedRows.length} hanging acls?`
            : `Are you sure that you want to delete acl '${acls[0].label}?`;

        const confirmationObject = {
            title: 'hanging_acl_delete',
            message: message,
            options: { translateText: false },
            callBack: this.deleteAcl.bind(this, acls)
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete the acl/s
     * @param acls
     * @private
     */
    private deleteAcl(acls: AclModel[]): Observable<any> {
        return new Observable((observer) => {
            this.notify.muteLocalNotifications();
            this.modalService.openModalInParent('progressModal');
            const idsToDelete = this.prepareAclData(acls);
            const dataToSubmit = from(idsToDelete || []);
            const translation = this.languageService.translate('hanging_acl_deleting');
            let index = 1;
            if (idsToDelete.length > 1) {
                this.progressMessage = `${translation} ${index} of ${idsToDelete.length}`;
            }
            const call = dataToSubmit.pipe(
                mergeMap((id: string) => this.deleteHangingAclHelper(id).pipe(catchError(error => of(error))))
            );

            call.subscribe({
                next: (): void => {
                    if (idsToDelete.length > 1) {
                        this.progressMessage = `${translation} ${index + 1} of ${idsToDelete.length}`;
                    }
                    index++;
                    observer.next();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HangingAclsModalComponent: unable to delete acl', error.message);
                    observer.error(error);
                },
                complete: (): void => {
                    this.notify.unmuteLocalNotifications();
                    this.loadingService.loadingActionDotsEmitter.next(false);
                    this.progressMessage = '';
                    this.modalService.close('progressModal');
                    this.updateView();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Helper function for deleting.
     * @param id
     * @private
     */
    private deleteHangingAclHelper(id: string): Observable<any> {
        return new Observable((observer) => {
            const deleteIndex = this.hangingAcls.findIndex(item => item.id === id);
            this.hangingAcls.splice(deleteIndex, 1);
            this.layer7ApiService.deleteAclRule(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('HangingAclsComponent: unable to delete acl', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Prepare the backend's data ready for deleting.
     * @param acls
     * @private
     */
    private prepareAclData(acls: AclModel[]): any {
        const idsToDelete = acls.length === 1
            ? [acls[0].id]
            : this.selectedRows.map((row: L7BackendServiceModel) => row.id);

        if (idsToDelete.length > 1) {
            this.submissionActive = true;
            this.progressMessage = 'Deleting hanging acls';
        }

        return idsToDelete;
    }

    /**
     * Helper function for updating the view.
     * @private
     */
    private updateView(): void {
        if (this.hangingAcls.length) {
            // Updated page and get a new list of backends.
            this.populateTableRows();

            // Reset the selectedRows variable when all selected rows have been deleted.
            this.selectedRows = [];
        } else {
            this.modalService.close('hangingAclsModal');
            this.notify.success('hanging_acls_deleted_all', { messageTranslate: true });
        }
    }

    /**
     * Populate the table rows.
     * @private
     */
    private populateTableRows(): void {
        this.tableRows = [];
        for (const acl of this.hangingAcls) {
            this.tableRows.push({
                id: acl.id,
                condition: this.aclService.matchReadable(acl.condition),
                type: this.aclService.conditionTypeReadable(acl.conditionType),
                redirectType: this.aclService.redirectTypeReadable(acl.redirectType),
                redirectLocation: this.aclService.getBackendName(acl, this.backends)
            });
        }
        this.tableRows = [...this.tableRows];
    }

    /**
     * Register buttons.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'hanging-acls-delete-acl': [{ path: '/haproxy/acls/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
